import { useState } from 'react'
import {
  Plug,
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Database,
  Wrench,
  RefreshCw,
  Trash2,
  Users,
  Server,
  Ban,
  Clock,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { api } from '@/lib/api'
import { ConfirmationModal } from '@/components/monitoring/ConfirmationModal'
import toast from 'react-hot-toast'

// ---------------------------------------------------------------------------
// Backend contract (src/modules/mcp/admin)
// ---------------------------------------------------------------------------

// These mirror `modules/mcp/admin/dto/mcp-admin.dto.ts` FIELD FOR FIELD. They
// drifted once already — the page read bare arrays where the API returns
// `{clients, total}` wrappers, and `clients.filter` threw during render. Check
// the DTO before changing anything here.

interface McpStatus {
  enabled: boolean
  reason: string | null
  dbRole: string
  toolCount: number
  database: {
    reachable: boolean
    latencyMs?: number | null
    error?: string | null
  }
}

interface McpClient {
  client_id: string
  client_name: string
  client_uri: string | null
  logo_uri: string | null
  /** true = cached Client ID Metadata Document, false = real DCR registration */
  from_metadata_document: boolean
  registration_kind?: string
  created_at: string
  last_seen_at: string | null
  revoked_at: string | null
  revoked: boolean
  live_refresh_tokens: number
}

/**
 * One live (user, client) connection.
 * Mirrors `McpConnection` in backend `modules/mcp/admin/dto/mcp-admin.dto.ts` —
 * keep the field names in step with it, they are not free-form.
 */
interface McpConnection {
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
  client_id: string
  /** Null when the client row was deleted but its tokens outlived it. */
  client_name: string | null
  live_token_count: number
  /** `created_at` of the most recently issued live token for this pair. */
  newest_token_created_at: string
  /** Furthest expiry among the pair's live tokens. */
  expires_at: string
}

interface CleanupResult {
  pending_deleted: number
  codes_deleted: number
  refresh_deleted: number
}

type ModalAction =
  | {
      type: 'revoke_user'
      userId: string
      displayName: string
      email: string
      clientName: string
    }
  | {
      type: 'revoke_client'
      clientId: string
      clientName: string
      fromMetadataDocument: boolean
      liveTokenCount: number
    }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDateTime = (value?: string | null) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString()
}

/** Human-readable distance from now, e.g. "in 6 days" / "3 hours ago". */
const relativeToNow = (value?: string | null) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  const diffMs = date.getTime() - Date.now()
  const past = diffMs < 0
  const abs = Math.abs(diffMs)
  const minutes = Math.round(abs / 60000)
  const hours = Math.round(abs / 3600000)
  const days = Math.round(abs / 86400000)

  let amount: string
  if (minutes < 60) amount = `${minutes} min`
  else if (hours < 48) amount = `${hours} hour${hours === 1 ? '' : 's'}`
  else amount = `${days} day${days === 1 ? '' : 's'}`

  return past ? `${amount} ago` : `in ${amount}`
}

const isExpired = (value?: string | null) => {
  if (!value) return false
  const date = new Date(value)
  return !Number.isNaN(date.getTime()) && date.getTime() < Date.now()
}

const displayNameOf = (connection: McpConnection) => {
  const full = [connection.first_name, connection.last_name].filter(Boolean).join(' ').trim()
  return full || connection.email
}

const getRoleColor = (role: string) => {
  switch (role) {
    case 'admin':
      return 'badge-danger'
    case 'analyst':
      return 'badge-info'
    default:
      return 'badge-gray'
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function McpAccess() {
  const { isAdmin, canMutate } = useAuth()
  const queryClient = useQueryClient()

  const [modalAction, setModalAction] = useState<ModalAction | null>(null)
  const [lastCleanup, setLastCleanup] = useState<CleanupResult | null>(null)

  // -- Queries --------------------------------------------------------------

  const {
    data: status,
    isLoading: statusLoading,
    isError: statusError,
    refetch: refetchStatus,
  } = useQuery<McpStatus>({
    queryKey: ['mcp-admin-status'],
    queryFn: async () => {
      const response = await api.get('/mcp/admin/status')
      return response.data
    },
    enabled: isAdmin,
    staleTime: 30000,
    refetchInterval: 60000,
  })

  const {
    data: connections = [],
    isLoading: connectionsLoading,
    refetch: refetchConnections,
  } = useQuery<McpConnection[]>({
    queryKey: ['mcp-admin-connections'],
    queryFn: async () => {
      const response = await api.get('/mcp/admin/connections')
      // Wrapper object, not a bare array.
      return response.data?.connections ?? []
    },
    enabled: isAdmin,
    staleTime: 30000,
    refetchInterval: 60000,
  })

  const {
    data: clients = [],
    isLoading: clientsLoading,
    refetch: refetchClients,
  } = useQuery<McpClient[]>({
    queryKey: ['mcp-admin-clients'],
    queryFn: async () => {
      const response = await api.get('/mcp/admin/clients')
      // Wrapper object, not a bare array — reading it as one crashed the page.
      return response.data?.clients ?? []
    },
    enabled: isAdmin,
    staleTime: 30000,
    refetchInterval: 60000,
  })

  const isRefreshing = statusLoading || connectionsLoading || clientsLoading

  const handleRefresh = () => {
    refetchStatus()
    refetchConnections()
    refetchClients()
  }

  // -- Mutations ------------------------------------------------------------

  const revokeUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.post(`/mcp/admin/users/${userId}/revoke`)
      return response.data as { tokens_revoked: number }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mcp-admin-connections'] })
      queryClient.invalidateQueries({ queryKey: ['mcp-admin-clients'] })
      setModalAction(null)
      toast.success(
        `Access revoked — ${data.tokens_revoked} token${data.tokens_revoked === 1 ? '' : 's'} invalidated`,
      )
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to revoke this connection')
    },
  })

  const revokeClientMutation = useMutation({
    mutationFn: async (clientId: string) => {
      const response = await api.post(`/mcp/admin/clients/${encodeURIComponent(clientId)}/revoke`)
      return response.data as { tokens_revoked: number }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mcp-admin-clients'] })
      queryClient.invalidateQueries({ queryKey: ['mcp-admin-connections'] })
      setModalAction(null)
      toast.success(
        `Client revoked — ${data.tokens_revoked} token${data.tokens_revoked === 1 ? '' : 's'} invalidated`,
      )
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to revoke this client')
    },
  })

  const cleanupMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/mcp/admin/cleanup')
      return response.data as CleanupResult
    },
    onSuccess: (data) => {
      setLastCleanup(data)
      queryClient.invalidateQueries({ queryKey: ['mcp-admin-connections'] })
      queryClient.invalidateQueries({ queryKey: ['mcp-admin-clients'] })
      const total = data.pending_deleted + data.codes_deleted + data.refresh_deleted
      toast.success(
        total === 0
          ? 'Nothing to clean up — no expired records were found'
          : `Cleaned up ${total} expired record${total === 1 ? '' : 's'}`,
      )
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Cleanup failed')
    },
  })

  const confirmModalAction = () => {
    if (!modalAction) return
    if (modalAction.type === 'revoke_user') {
      revokeUserMutation.mutate(modalAction.userId)
    } else {
      revokeClientMutation.mutate(modalAction.clientId)
    }
  }

  // -- Access gate ----------------------------------------------------------

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Shield className="h-16 w-16 mx-auto mb-4 text-secondary-400" />
        <h3 className="text-lg font-medium text-secondary-900 mb-2">Access Denied</h3>
        <p className="text-secondary-500">You don't have permission to manage MCP access.</p>
      </div>
    )
  }

  const activeClients = clients.filter((client) => !client.revoked_at)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-secondary-900 flex items-center">
            <Plug className="h-7 w-7 mr-2 text-primary-600" />
            MCP Access
          </h2>
          <p className="text-secondary-600 mt-1">
            Who can reach SDI data from an AI client, and through which application
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className="btn-secondary flex items-center"
          disabled={isRefreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Status banner */}
      <StatusBanner status={status} isLoading={statusLoading} isError={statusError} />

      {/* Connections */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
              <Users className="h-5 w-5 mr-2 text-primary-600" />
              Active connections
            </h3>
            <p className="text-sm text-secondary-500 mt-0.5">
              People with a live token right now. Revoking one signs that person out of MCP
              immediately.
            </p>
          </div>
          <span className="status-badge badge-gray whitespace-nowrap">
            {connections.length} connected
          </span>
        </div>

        {connectionsLoading ? (
          <TableSkeleton rows={3} />
        ) : connections.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nobody has connected yet."
            body="Once a teammate adds the SDI server to their AI client and signs in with their SDI account, their session shows up here with the client they used and when it expires."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50 sticky top-0 z-10">
                <tr>
                  <Th>Person</Th>
                  <Th>Role</Th>
                  <Th>Client</Th>
                  {/* NOT "connected since": this is the newest live token's
                      created_at, and refresh-token rotation moves it forward on
                      every refresh. Labelling it as a connection start date
                      would quietly lie about how long someone has been on. */}
                  <Th>Last token issued</Th>
                  <Th>Expires</Th>
                  {canMutate && <Th align="right">Actions</Th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {connections.map((connection) => {
                  const expired = isExpired(connection.expires_at)
                  return (
                    <tr
                      key={`${connection.user_id}-${connection.client_id}`}
                      className="hover:bg-secondary-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-secondary-900">
                          {displayNameOf(connection)}
                        </div>
                        <div className="text-sm text-secondary-500">{connection.email}</div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`status-badge ${getRoleColor(connection.role)}`}>
                          {connection.role}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-secondary-900">{connection.client_name}</div>
                        <div
                          className="text-xs text-secondary-400 font-mono truncate max-w-[16rem]"
                          title={connection.client_id}
                        >
                          {connection.client_id}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                        {formatDateTime(connection.newest_token_created_at)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className={expired ? 'text-danger-600 font-medium' : 'text-secondary-500'}>
                          {formatDateTime(connection.expires_at)}
                        </div>
                        <div className={`text-xs ${expired ? 'text-danger-500' : 'text-secondary-400'}`}>
                          {expired ? 'expired' : relativeToNow(connection.expires_at)}
                        </div>
                      </td>

                      {canMutate && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() =>
                              setModalAction({
                                type: 'revoke_user',
                                userId: connection.user_id,
                                displayName: displayNameOf(connection),
                                email: connection.email,
                                clientName: connection.client_name,
                              })
                            }
                            className="inline-flex items-center text-danger-600 hover:text-danger-900"
                            disabled={revokeUserMutation.isLoading}
                          >
                            <Ban className="h-4 w-4 mr-1" />
                            Revoke
                          </button>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Clients */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
              <Server className="h-5 w-5 mr-2 text-primary-600" />
              Registered clients
            </h3>
            <p className="text-sm text-secondary-500 mt-0.5">
              The applications allowed to ask for an SDI token.
            </p>
          </div>
          <span className="status-badge badge-gray whitespace-nowrap">
            {activeClients.length} active
          </span>
        </div>

        {/* What the two kinds mean */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-4">
            <span className="status-badge badge-info mb-2 inline-flex">Registered (DCR)</span>
            <p className="text-sm text-secondary-600">
              The client registered itself with us and holds real credentials. Revoking it destroys
              that registration — the client has to register again before anyone can reconnect.
            </p>
          </div>
          <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-4">
            <span className="status-badge badge-gray mb-2 inline-flex">Metadata document</span>
            <p className="text-sm text-secondary-600">
              No registration exists — we only cached the client's published metadata document.
              Revoking it just clears that cache and kills live tokens; the client can come straight
              back on the next connection.
            </p>
          </div>
        </div>

        {clientsLoading ? (
          <TableSkeleton rows={2} />
        ) : clients.length === 0 ? (
          <EmptyState
            icon={Server}
            title="No client has registered yet."
            body="Clients appear here the first time someone points an AI application at the SDI MCP endpoint and completes sign-in. Nothing to do until then."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50 sticky top-0 z-10">
                <tr>
                  <Th>Client</Th>
                  <Th>Kind</Th>
                  <Th>Live tokens</Th>
                  <Th>First seen</Th>
                  <Th>Last seen</Th>
                  {canMutate && <Th align="right">Actions</Th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {clients.map((client) => (
                  <tr key={client.client_id} className="hover:bg-secondary-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {client.logo_uri ? (
                          <img
                            src={client.logo_uri}
                            alt=""
                            className="w-8 h-8 rounded-lg mr-3 object-contain bg-secondary-100"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                            <Plug className="h-4 w-4 text-primary-600" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-secondary-900 flex items-center gap-2">
                            {client.client_name}
                            {client.revoked_at && (
                              <span className="status-badge badge-danger">Revoked</span>
                            )}
                          </div>
                          {client.client_uri && (
                            <a
                              href={client.client_uri}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="text-xs text-primary-600 hover:text-primary-800 truncate block max-w-[18rem]"
                            >
                              {client.client_uri}
                            </a>
                          )}
                          <div
                            className="text-xs text-secondary-400 font-mono truncate max-w-[18rem]"
                            title={client.client_id}
                          >
                            {client.client_id}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {client.from_metadata_document ? (
                        <span
                          className="status-badge badge-gray"
                          title="Cached Client ID Metadata Document — no stored registration"
                        >
                          Metadata document
                        </span>
                      ) : (
                        <span
                          className="status-badge badge-info"
                          title="Dynamic Client Registration — real stored registration"
                        >
                          Registered (DCR)
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`status-badge ${
                          client.live_refresh_tokens > 0 ? 'badge-success' : 'badge-gray'
                        }`}
                      >
                        {client.live_refresh_tokens}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                      {formatDate(client.created_at)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="text-secondary-500">{formatDateTime(client.last_seen_at)}</div>
                      {client.last_seen_at && (
                        <div className="text-xs text-secondary-400">
                          {relativeToNow(client.last_seen_at)}
                        </div>
                      )}
                    </td>

                    {canMutate && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {client.revoked_at ? (
                          <span className="text-xs text-secondary-400">
                            Revoked {formatDate(client.revoked_at)}
                          </span>
                        ) : (
                          <button
                            onClick={() =>
                              setModalAction({
                                type: 'revoke_client',
                                clientId: client.client_id,
                                clientName: client.client_name,
                                fromMetadataDocument: client.from_metadata_document,
                                liveTokenCount: client.live_refresh_tokens,
                              })
                            }
                            className="inline-flex items-center text-danger-600 hover:text-danger-900"
                            disabled={revokeClientMutation.isLoading}
                          >
                            <Ban className="h-4 w-4 mr-1" />
                            Revoke
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Maintenance */}
      {canMutate && (
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-primary-600" />
                Housekeeping
              </h3>
              <p className="text-sm text-secondary-500 mt-0.5 max-w-2xl">
                Deletes expired authorization records — half-finished sign-ins, used one-time codes
                and refresh tokens that are already past their expiry. Nobody loses a working
                connection.
              </p>
            </div>
            <button
              onClick={() => cleanupMutation.mutate()}
              className="btn-secondary flex items-center whitespace-nowrap"
              disabled={cleanupMutation.isLoading}
            >
              <Trash2 className={`h-4 w-4 mr-2 ${cleanupMutation.isLoading ? 'animate-pulse' : ''}`} />
              {cleanupMutation.isLoading ? 'Cleaning up...' : 'Clean up expired records'}
            </button>
          </div>

          {lastCleanup && (
            <div className="mt-4 rounded-xl border border-secondary-200 bg-secondary-50 p-4">
              <p className="text-sm font-medium text-secondary-900 mb-3">Last cleanup removed</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <CleanupStat label="Unfinished sign-ins" value={lastCleanup.pending_deleted} />
                <CleanupStat label="Authorization codes" value={lastCleanup.codes_deleted} />
                <CleanupStat label="Refresh tokens" value={lastCleanup.refresh_deleted} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation modal */}
      {modalAction && (
        <ConfirmationModal
          isOpen={!!modalAction}
          onClose={() => setModalAction(null)}
          onConfirm={confirmModalAction}
          title={
            modalAction.type === 'revoke_user' ? 'Revoke this connection?' : 'Revoke this client?'
          }
          message={
            modalAction.type === 'revoke_user'
              ? `${modalAction.displayName} will lose MCP access immediately and their AI client will start failing until they sign in again.`
              : modalAction.fromMetadataDocument
                ? `This clears the cached metadata document for ${modalAction.clientName} and kills its live tokens. The client can reconnect straight away — use this to force a re-read of its metadata, not to keep it out.`
                : `This destroys the stored registration for ${modalAction.clientName} and kills its live tokens. Everyone using it is signed out, and the client must register again before anyone can reconnect.`
          }
          confirmLabel="Revoke access"
          confirmVariant="danger"
          isLoading={revokeUserMutation.isLoading || revokeClientMutation.isLoading}
          requireConfirmText="REVOKE"
        >
          <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-4 space-y-2">
            {modalAction.type === 'revoke_user' ? (
              <>
                <ModalRow label="Person" value={modalAction.displayName} />
                <ModalRow label="Email" value={modalAction.email} />
                <ModalRow label="Client" value={modalAction.clientName} />
              </>
            ) : (
              <>
                <ModalRow label="Client" value={modalAction.clientName} />
                <ModalRow
                  label="Kind"
                  value={
                    modalAction.fromMetadataDocument
                      ? 'Metadata document (cache only)'
                      : 'Registered (DCR)'
                  }
                />
                <ModalRow label="Live tokens" value={String(modalAction.liveTokenCount)} />
              </>
            )}
          </div>
        </ConfirmationModal>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Status banner
// ---------------------------------------------------------------------------

function StatusBanner({
  status,
  isLoading,
  isError,
}: {
  status?: McpStatus
  isLoading: boolean
  isError: boolean
}) {
  if (isLoading && !status) {
    return (
      <div className="card animate-pulse">
        <div className="h-6 w-56 bg-secondary-200 rounded mb-3" />
        <div className="h-4 w-full max-w-xl bg-secondary-100 rounded" />
      </div>
    )
  }

  if (isError || !status) {
    return (
      <div className="rounded-xl border-2 border-danger-200 bg-danger-50 p-6">
        <div className="flex items-start">
          <div className="w-10 h-10 rounded-full bg-danger-100 flex items-center justify-center mr-4 flex-shrink-0">
            <ShieldAlert className="h-5 w-5 text-danger-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-danger-900">
              The MCP admin API did not answer
            </h3>
            <p className="text-sm text-danger-700 mt-1 max-w-3xl">
              We could not read the MCP status from the backend, so nothing below can be trusted.
              Either the MCP module is not deployed on this environment, or the API is down. Check
              the backend logs before changing anything here.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (!status.enabled) {
    return (
      <div className="rounded-xl border-2 border-danger-200 bg-danger-50 p-6">
        <div className="flex items-start">
          <div className="w-10 h-10 rounded-full bg-danger-100 flex items-center justify-center mr-4 flex-shrink-0">
            <XCircle className="h-5 w-5 text-danger-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-danger-900">MCP access is switched off</h3>
            <p className="text-sm text-danger-700 mt-1">
              No AI client can reach SDI data right now. Existing connections will fail.
            </p>
            <div className="mt-4 rounded-lg bg-white border border-danger-200 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-danger-500 mb-1">
                Why
              </p>
              <p className="text-sm text-danger-900 font-medium break-words">
                {status.reason || 'No reason was reported by the backend.'}
              </p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <StatusChip
                icon={Database}
                label="Database"
                value={status.database?.reachable ? 'reachable' : 'unreachable'}
                tone={status.database?.reachable ? 'neutral' : 'danger'}
              />
              <StatusChip icon={Shield} label="DB role" value={status.dbRole || '—'} tone="neutral" />
              <StatusChip
                icon={Wrench}
                label="Tools"
                value={String(status.toolCount ?? 0)}
                tone="neutral"
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Enabled
  return (
    <div className="rounded-xl border-2 border-success-200 bg-success-50 p-6">
      <div className="flex items-start">
        <div className="w-10 h-10 rounded-full bg-success-100 flex items-center justify-center mr-4 flex-shrink-0">
          <ShieldCheck className="h-5 w-5 text-success-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-success-900 flex items-center">
            MCP access is enabled
            <CheckCircle className="h-4 w-4 ml-2 text-success-600" />
          </h3>
          <p className="text-sm text-success-800 mt-1 max-w-3xl">
            Approved AI clients can query SDI through a dedicated read-only database role. That role
            cannot read the users table, the audit logs, or the scraping staging table — and it
            cannot write anything back.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <StatusChip icon={Shield} label="DB role" value={status.dbRole || '—'} tone="success" />
            <StatusChip
              icon={Wrench}
              label="Tools exposed"
              value={String(status.toolCount ?? 0)}
              tone="success"
            />
            <StatusChip
              icon={Database}
              label="Database"
              value={status.database?.reachable ? 'reachable' : 'unreachable'}
              tone={status.database?.reachable ? 'success' : 'danger'}
            />
          </div>

          {!status.database?.reachable && (
            <div className="mt-4 flex items-start rounded-lg bg-white border border-warning-200 p-4">
              <AlertTriangle className="h-4 w-4 text-warning-600 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-warning-900">
                MCP is enabled but the read-only database connection is not answering. Queries will
                fail until it recovers, even though the switch is on.
              </p>
            </div>
          )}

          {status.reason && (
            <p className="text-xs text-success-700 mt-3">{status.reason}</p>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function StatusChip({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: React.ElementType
  label: string
  value: string
  tone: 'success' | 'danger' | 'neutral'
}) {
  const toneClass =
    tone === 'success'
      ? 'bg-white border-success-200 text-success-900'
      : tone === 'danger'
        ? 'bg-white border-danger-200 text-danger-900'
        : 'bg-white border-secondary-200 text-secondary-900'

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${toneClass}`}
    >
      <Icon className="h-4 w-4 opacity-70" />
      <span className="text-xs uppercase tracking-wider opacity-70">{label}</span>
      <span className="font-mono font-medium">{value}</span>
    </span>
  )
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode
  align?: 'left' | 'right'
}) {
  return (
    <th
      className={`px-6 py-3 ${align === 'right' ? 'text-right' : 'text-left'} text-xs font-medium text-secondary-500 uppercase tracking-wider`}
    >
      {children}
    </th>
  )
}

function EmptyState({
  icon: Icon,
  title,
  body,
}: {
  icon: React.ElementType
  title: string
  body: string
}) {
  return (
    <div className="text-center py-12">
      <Icon className="h-14 w-14 mx-auto mb-4 text-secondary-300" />
      <h4 className="text-base font-medium text-secondary-900 mb-2">{title}</h4>
      <p className="text-secondary-500 text-sm max-w-xl mx-auto">{body}</p>
    </div>
  )
}

function TableSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3 animate-pulse py-2">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="h-12 bg-secondary-100 rounded-lg" />
      ))}
    </div>
  )
}

function CleanupStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-white border border-secondary-200 px-4 py-3">
      <div className="text-2xl font-bold text-secondary-900">{value}</div>
      <div className="text-xs text-secondary-500 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  )
}

function ModalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-secondary-600 flex-shrink-0">{label}</span>
      <span className="font-medium text-secondary-900 truncate" title={value}>
        {value}
      </span>
    </div>
  )
}
